import { computed, inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';

import { MatSnackBar } from '@angular/material/snack-bar';

import { filter, firstValueFrom, take } from 'rxjs';

import {
  VIEW_ORGANIZATION_CAN_EDIT_PERMISSION,
  VIEW_ORGANIZATION_CAN_VIEW_PERMISSION,
} from '@data-access/permissions';
import { PermissionsService } from '@shared/services/permissions.service';
import { UserService } from '@user/user.service';
import { User } from '@iris-sites/interfaces/sites.interfaces';

/**
 * Guard that checks if the user has permission to access the organization detail page.
 *
 * - Internal users: Checked via global role permissions (VIEW_ORGANIZATION tokens).
 * - External users: Must have an explicit organization membership matching the org ID
 *   in the URL. The user's `organization_id` field identifies the single organization
 *   they are an Organization Administrator for.
 *
 * Why async / await with Signal → Observable → Promise conversion (for internal users):
 *
 * The permission tokens (VIEW_ORGANIZATION_CAN_VIEW_PERMISSION, VIEW_ORGANIZATION_CAN_EDIT_PERMISSION)
 * are Angular Signals created via `toSignal()` inside `permissionProviders`. Under the hood,
 * `toSignal()` wraps an Observable (from PermissionsService.observeUserPermissions) and the
 * resulting Signal starts with an initial value of `undefined` until the first HTTP response
 * arrives with the actual permission data.
 *
 * If we read the Signal synchronously (e.g. `_canView()`), we would always get `undefined`
 * on the first check because the permission data hasn't loaded yet. This would cause the
 * guard to incorrectly block access every time.
 *
 * To solve this, we need to *wait* for the permission data to arrive:
 *
 * 1. `computed()` — Combines both permission Signals into a single derived Signal (`_canAccess`).
 *
 * 2. `toObservable()` — Angular provides no direct Signal-to-Promise API. The only way to
 *    reactively subscribe to a Signal outside of a template is to convert it to an Observable
 *    using `toObservable()` from `@angular/core/rxjs-interop`.
 *
 * 3. `filter(value !== undefined)` — Skips the initial `undefined` emission so we only
 *    evaluate once the real permission boolean has arrived.
 *
 * 4. `firstValueFrom()` — Converts the Observable into a Promise that resolves with the
 *    first emitted value (after the filter). This allows us to `await` it in the guard.
 *
 * Why try/catch:
 *
 * `firstValueFrom()` throws an `EmptyError` if the Observable completes without emitting
 * any value (e.g. if the permission Observable errors out or completes before emitting a
 * non-undefined value). The catch block ensures the guard fails safely by redirecting the
 * user to the organizations list rather than crashing with an unhandled promise rejection.
 */
export const orgDetailAccessGuard: CanMatchFn = async (
  _route: Route,
  segments: UrlSegment[],
) => {
  const _router = inject(Router);
  const _snackBar = inject(MatSnackBar);
  const _canView = inject(VIEW_ORGANIZATION_CAN_VIEW_PERMISSION);
  const _canEdit = inject(VIEW_ORGANIZATION_CAN_EDIT_PERMISSION);
  const _permissionsService = inject(PermissionsService);
  const _userService = inject(UserService);

  const _openSnackBar = () =>
    _snackBar.open(
      `You do not have permissions to access this organization page`,
      'Close',
      {
        duration: 5000,
      },
    );

  try {
    const _canAccess$ = toObservable(
      computed(() => _canView() || _canEdit()),
    ).pipe(filter((v): v is boolean => v !== undefined));

    const [canAccess, _isInternal, user] = await Promise.all([
      firstValueFrom(_canAccess$),
      firstValueFrom(_permissionsService.isInternal$.pipe(take(1))),
      firstValueFrom(
        _userService.user$.pipe(
          filter((u): u is User => !!u?.id),
          take(1),
        ),
      ),
    ]);

    if (!canAccess) {
      _openSnackBar();
      return _router.createUrlTree([
        _isInternal ? '/organizations' : '/welcome',
      ]);
    }

    // External users must also belong to the specific organization in the URL
    if (!_isInternal) {
      const orgIdFromUrl = segments[0]?.path;
      if (!orgIdFromUrl || user.organization_id !== orgIdFromUrl) {
        _openSnackBar();
        return _router.createUrlTree(['/welcome']);
      }
    }

    return true;
  } catch (error) {
    console.error('Error occurred during navigation', error);
    _openSnackBar();
    return _router.createUrlTree(['/welcome']);
  }
};
